import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Markets.css';

const Crypto: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const majorCryptos = [
    { name: 'Bitcoin (BTC)', description: 'First and largest cryptocurrency by market cap', icon: '₿' },
    { name: 'Ethereum (ETH)', description: 'Leading smart contract platform', icon: 'Ξ' },
    { name: 'Ripple (XRP)', description: 'Digital payment protocol for cross-border transfers', icon: '✕' },
    { name: 'Litecoin (LTC)', description: 'Silver to Bitcoin\'s gold, faster transactions', icon: 'Ł' },
    { name: 'Bitcoin Cash (BCH)', description: 'Bitcoin fork focused on scalability', icon: '฿' },
    { name: 'Cardano (ADA)', description: 'Research-driven blockchain platform', icon: '₳' },
    { name: 'Polkadot (DOT)', description: 'Multi-chain network enabling interoperability', icon: '●' },
    { name: 'Solana (SOL)', description: 'High-performance blockchain for DeFi and NFTs', icon: '◎' }
  ];

  return (
    <motion.div 
      className="markets-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Cryptocurrency Trading</h1>
          <p>Trade major cryptocurrencies with professional signals</p>
        </motion.div>

        

        <motion.section className="market-overview" variants={itemVariants}>
          <h2>What is Cryptocurrency Trading?</h2>
          <p>
            Cryptocurrency trading involves buying and selling digital currencies on specialized exchanges or through CFDs (Contracts for Difference). 
            Unlike traditional currencies, cryptocurrencies are decentralized, operate on blockchain technology, and are not controlled by any government or central authority.
          </p>
          <p>
            The crypto market operates 24/7, 365 days a year, offering unprecedented trading opportunities. With high volatility comes both 
            significant risk and potential reward, making proper risk management essential for success.
          </p>
        </motion.section>

        <motion.section className="market-features" variants={itemVariants}>
          <h2>Why Trade Cryptocurrencies?</h2>
          <div className="features-grid">
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>24/7 Trading</h3>
              <p>Trade any time, any day—the crypto market never closes</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>High Volatility</h3>
              <p>Significant price movements create trading opportunities</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Decentralization</h3>
              <p>Freedom from traditional banking systems and government control</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Innovation & Growth</h3>
              <p>Exposure to cutting-edge blockchain technology and DeFi</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Leverage Trading</h3>
              <p>Amplify positions with leverage through CFDs and futures</p>
            </motion.div>
            
            <motion.div className="feature" variants={itemVariants} whileHover={{ scale: 1.05 }}>
              <h3>Portfolio Diversification</h3>
              <p>Low correlation with traditional assets enhances diversification</p>
            </motion.div>
          </div>
        </motion.section>

        <motion.section className="crypto-list" variants={itemVariants}>
          <h2>Major Cryptocurrencies</h2>
          <div className="crypto-grid">
            {majorCryptos.map((crypto, i) => (
              <motion.div key={i} className="crypto-card" variants={itemVariants} whileHover={{ scale: 1.03 }}>
                <div className="crypto-icon">{crypto.icon}</div>
                <h3>{crypto.name}</h3>
                <p>{crypto.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="market-factors" variants={itemVariants}>
          <h2>Factors Affecting Crypto Prices</h2>
          <div className="factors-grid">
            <div className="factor">
              <h4>📊 Supply & Demand</h4>
              <p>Limited supply (Bitcoin's 21M cap) and growing adoption drive prices</p>
            </div>
            <div className="factor">
              <h4>📰 News & Sentiment</h4>
              <p>Media coverage, influencer tweets, and market sentiment</p>
            </div>
            <div className="factor">
              <h4>⚖️ Regulation</h4>
              <p>Government policies, legal frameworks, and institutional adoption</p>
            </div>
            <div className="factor">
              <h4>🔧 Technology</h4>
              <p>Network upgrades, scalability solutions, and security</p>
            </div>
            <div className="factor">
              <h4>🏦 Institutional Investment</h4>
              <p>Corporate treasuries, ETFs, and traditional finance adoption</p>
            </div>
            <div className="factor">
              <h4>⛏️ Mining & Halving</h4>
              <p>Bitcoin halving events and mining difficulty adjustments</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="trading-strategies" variants={itemVariants}>
          <h2>Crypto Trading Strategies</h2>
          <div className="strategies-grid">
            <div className="strategy">
              <h3>📈 HODLing</h3>
              <p>Long-term buy-and-hold strategy believing in crypto's future</p>
            </div>
            <div className="strategy">
              <h3>📊 Day Trading</h3>
              <p>Capitalize on intraday volatility with multiple trades per day</p>
            </div>
            <div className="strategy">
              <h3>🔄 Swing Trading</h3>
              <p>Hold positions for days or weeks to profit from price swings</p>
            </div>
            <div className="strategy">
              <h3>⚡ Scalping</h3>
              <p>Make numerous small profits from tiny price movements</p>
            </div>
            <div className="strategy">
              <h3>🤖 Automated Trading</h3>
              <p>Use trading bots and algorithms to execute strategies</p>
            </div>
            <div className="strategy">
              <h3>💎 DCA Strategy</h3>
              <p>Dollar-cost averaging to reduce impact of volatility</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="risk-warning" variants={itemVariants}>
          <h2>⚠️ Important Risk Considerations</h2>
          <div className="warning-box">
            <p><strong>Extreme Volatility:</strong> Crypto prices can swing 10-20% or more in a single day</p>
            <p><strong>Regulatory Risk:</strong> Government regulations can significantly impact prices</p>
            <p><strong>Security Risks:</strong> Exchange hacks, wallet security, and scams are real threats</p>
            <p><strong>Market Manipulation:</strong> "Pump and dump" schemes and whale manipulation</p>
            <p><strong>Liquidity Issues:</strong> Some altcoins may have low liquidity during market stress</p>
            <p><strong>Never Invest More Than You Can Afford to Lose</strong></p>
          </div>
        </motion.section>

        <motion.section className="trading-tips" variants={itemVariants}>
          <h2>Cryptocurrency Trading Tips</h2>
          <ul className="tips-list">
            <li>Start with major cryptocurrencies (BTC, ETH) before exploring altcoins</li>
            <li>Use hardware wallets for long-term holdings, not exchange wallets</li>
            <li>Never share your private keys or seed phrases with anyone</li>
            <li>Use two-factor authentication (2FA) on all exchange accounts</li>
            <li>Beware of "too good to be true" promises and fake giveaways</li>
            <li>Research projects thoroughly—read whitepapers and check team backgrounds</li>
            <li>Set strict stop-losses due to extreme volatility</li>
            <li>Keep emotions in check—FOMO and FUD lead to poor decisions</li>
            <li>Stay updated on regulatory developments in major markets</li>
            <li>Practice with small amounts before committing significant capital</li>
          </ul>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Start Trading Crypto with Confidence</h2>
          <p>Get professional crypto trading signals and expert market analysis</p>
          <div className="btn-container">
            <Link to="/services/signals" className="btn">Get Trading Signals</Link>
            <Link to="/education" className="btn secondary">Learn More</Link>
            <Link to="/ib-partners" className="btn secondary">Choose Your Broker</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Crypto;
